import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button } from "src/components/form";
import { ButtonVariant } from "src/components/form/Button";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background-color: #f8f9fa;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 48px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #212529;
`;

const Message = styled.p`
  font-size: 16px;
  line-height: 1.5;
  color: #6c757d;
  margin-bottom: 32px;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background-color: #28a745;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 24px;
  border-radius: 50%;
  background-color: #dc3545;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
`;

const PayPalOnboardingCallback: React.FC = () => {
  const [status, setStatus] = useState<"success" | "error" | "loading">(
    "loading"
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const merchantId = urlParams.get("merchantId");
    const merchantIdInPayPal = urlParams.get("merchantIdInPayPal");
    const permissionsGranted = urlParams.get("permissionsGranted");
    const consentStatus = urlParams.get("consentStatus");
    const isSuccess =
      merchantId &&
      merchantIdInPayPal &&
      (permissionsGranted === "true" || consentStatus === "true");

    const resultStatus = isSuccess ? "success" : "error";
    setStatus(resultStatus);

    if (window.opener) {
      window.opener.postMessage(
        {
          source: "paypal-onboarding-complete",
          data: { state: resultStatus },
        },
        window.location.origin
      );
    }

    if (isSuccess) {
      const timer = setTimeout(() => {
        window.close();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    window.close();
  };

  if (status === "loading") {
    return (
      <Container>
        <Card>
          <Title>Processing...</Title>
          <Message>Please wait while we complete your PayPal setup.</Message>
        </Card>
      </Container>
    );
  }

  if (status === "success") {
    return (
      <Container>
        <Card>
          <SuccessIcon>✓</SuccessIcon>
          <Title>PayPal Connected Successfully!</Title>
          <Message>
            Your PayPal account is connected. This invoice can now be paid with
            PayPal.
          </Message>
          <Message style={{ fontSize: "14px", marginBottom: "16px" }}>
            This window will close automatically in 3 seconds...
          </Message>
          <Button
            type="button"
            variant={ButtonVariant.primary}
            onClick={handleClose}
          >
            Close
          </Button>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <ErrorIcon>✕</ErrorIcon>
        <Title>PayPal Connection Failed</Title>
        <Message>
          There was an issue connecting your PayPal account. Please try again or
          contact support if the problem persists.
        </Message>
        <Button
          type="button"
          variant={ButtonVariant.primary}
          onClick={handleClose}
        >
          Close
        </Button>
      </Card>
    </Container>
  );
};

export default PayPalOnboardingCallback;
